import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, Square, Loader2, X, Check, Sparkles, AlertCircle, Wand2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Section-level dictations are typically <2 min. Cap at 3 min so even
// long history-taking sessions fit comfortably under Whisper's 25 MB limit.
const MAX_SECTION_MS = 3 * 60 * 1000;

const SECTION_LABEL = {
  chief_complaints:     'Chief Complaints',
  past_history:         'Past History',
  clinical_examination: 'Clinical Examination',
  treatment_plan:       'Treatment Plan',
  provisional_diagnosis:'Provisional Diagnosis',
  final_diagnosis:      'Final Diagnosis',
  visit_notes:          'Visit Notes',
};

/**
 * SectionDictationButton — preview-first replacement for the inline mic.
 *
 * Recording UX:
 *   • Mic button in the toolbar mirrors the old look.
 *   • While recording, a dark floating bubble shows the live Web Speech API
 *     partial (browser-native, on-device) so the doctor can SEE that their
 *     voice is being picked up — no more "did it hear me?" pauses.
 *   • Stop → preview modal opens immediately.
 *
 * Two-stage transcription (matches Full Visit Dictation):
 *   1. POST /api/transcribe (audio only) → raw Whisper text  (~1 s)
 *      → modal shows the raw text as soon as it lands.
 *   2. POST /api/transcribe/refine {raw_text, section_type}  (~2-3 s)
 *      → modal updates to the section-tailored refined text.
 *   The doctor can toggle raw ↔ refined, edit inline, then Apply.
 */
export default function SectionDictationButton({
  sectionType = '',
  onApply,            // (text:string) => void
  className = '',
  size = 'sm',
  title,
  buttonComponent,    // optional render-prop: ({ recording, processing, onClick }) => ReactNode
}) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [livePartial, setLivePartial] = useState('');

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [rawText, setRawText] = useState('');
  const [refinedText, setRefinedText] = useState('');
  const [refining, setRefining] = useState(false);
  const [view, setView] = useState('refined');   // 'raw' | 'refined'
  const [edited, setEdited] = useState('');

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const autoStopRef = useRef(null);
  const speechRecRef = useRef(null);

  // ---------------- Cleanup ----------------
  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (speechRecRef.current) {
      try { speechRecRef.current.stop(); } catch (_) { /* ignore */ }
      speechRecRef.current = null;
    }
    recorderRef.current = null;
  }, []);
  useEffect(() => () => cleanup(), [cleanup]);

  // ---------------- Live Web Speech preview ----------------
  const startLivePreview = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = 'en-IN';
      rec.onresult = (e) => {
        let live = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          live += e.results[i][0].transcript;
        }
        setLivePartial((prev) => (prev + ' ' + live).trim().slice(-400));
      };
      rec.onerror = () => { /* best-effort */ };
      rec.start();
      speechRecRef.current = rec;
    } catch (_) { /* no-op */ }
  }, []);

  // ---------------- Recording lifecycle ----------------
  const startRecording = async () => {
    setElapsed(0); setLivePartial(''); chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
      });
      streamRef.current = stream;

      let mime = 'audio/webm;codecs=opus';
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) mime = 'audio/webm';
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) mime = 'audio/mp4';

      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        cleanup();
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        setRecording(false);
        if (blob.size < 1000) {
          toast.info('Recording was too short. Speak for at least a few seconds.');
          return;
        }
        await sendForTranscription(blob, mime);
      };

      recorder.start(250);
      setRecording(true);
      startLivePreview();

      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      autoStopRef.current = setTimeout(() => {
        if (recorderRef.current?.state === 'recording') {
          recorderRef.current.stop();
          toast.info('Auto-stopped at 3 minutes.');
        }
      }, MAX_SECTION_MS);
    } catch (err) {
      console.error('Mic error:', err);
      toast.error('Microphone access denied. Please allow microphone in browser settings.');
      cleanup();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  // ---------------- 2-stage transcribe → refine ----------------
  const sendForTranscription = async (blob, mime) => {
    setUploading(true);
    setPreviewOpen(true);
    setRawText('');
    setRefinedText('');
    setEdited('');
    setView('refined');
    try {
      // Stage 1: Whisper-only (no section_type) → fastest possible raw text
      const fd = new FormData();
      fd.append('audio', blob, mime.includes('mp4') ? 'voice.mp4' : 'voice.webm');
      const tr = await axios.post(`${API_URL}/api/transcribe`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const raw = (tr.data?.text || '').trim();
      if (!raw) {
        toast.warning('Could not detect any speech. Please try again.');
        setPreviewOpen(false);
        return;
      }
      setRawText(raw);
      setEdited(raw);
      setUploading(false);

      // Stage 2: section-aware refinement (only when sectionType makes sense)
      if (sectionType && SECTION_LABEL[sectionType]) {
        setRefining(true);
        try {
          const rf = await axios.post(`${API_URL}/api/transcribe/refine`, {
            raw_text: raw,
            section_type: sectionType,
          }, { withCredentials: true });
          const refined = (rf.data?.text || '').trim();
          const wasRefined = !!rf.data?.refined && refined && refined !== raw;
          setRefinedText(wasRefined ? refined : '');
          // Auto-prefer refined view when refinement actually changed things.
          if (wasRefined) {
            setView('refined');
            setEdited(refined);
          } else {
            setView('raw');
            setEdited(raw);
          }
        } catch (e) {
          // Refinement failure is non-fatal — doctor still has the raw text.
          setRefinedText('');
          setView('raw');
          setEdited(raw);
        } finally {
          setRefining(false);
        }
      } else {
        setView('raw');
        setEdited(raw);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Transcription failed. Please try again.');
      setPreviewOpen(false);
    } finally {
      setUploading(false);
    }
  };

  const handleApply = () => {
    const text = (edited || '').trim();
    if (!text) {
      toast.info('Nothing to apply.');
      return;
    }
    onApply?.(text);
    setPreviewOpen(false);
    toast.success('Dictation applied.');
  };

  const onClick = () => {
    if (uploading || refining) return;
    if (recording) stopRecording();
    else startRecording();
  };

  // ---------------- Render ----------------
  const processing = uploading || refining;
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  const renderedButton = buttonComponent
    ? buttonComponent({ recording, processing, onClick })
    : (
      <Button
        type="button"
        size={size}
        onClick={onClick}
        disabled={processing && !previewOpen}
        className={`gap-2 transition ${
          recording
            ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse'
            : processing
              ? 'bg-iith-navyMid text-white'
              : 'bg-white border border-iith-navy/15 hover:bg-iith-mist text-iith-navy'
        } ${className}`}
        title={title || (sectionType === 'chief_complaints'
          ? 'Dictate patient conversation — AI will extract chief complaints'
          : 'Dictate this section — review preview before applying')}
        data-testid={`section-dictate-btn-${sectionType || 'generic'}`}
      >
        {processing ? <Loader2 className="h-4 w-4 animate-spin" /> :
          recording ? <Square className="h-4 w-4 fill-current" /> :
          <Mic className="h-4 w-4" />}
      </Button>
    );

  return (
    <>
      {renderedButton}

      {/* Floating live transcript bubble while recording */}
      {recording && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] max-w-xl w-[calc(100%-3rem)] bg-slate-900/95 backdrop-blur-md text-white rounded-2xl shadow-2xl px-5 py-3 border border-white/10"
          data-testid="section-live-partial"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold">
              Listening · {mm}:{ss} / 03:00
              {sectionType && SECTION_LABEL[sectionType] && (
                <span className="ml-1.5 text-white/40">· {SECTION_LABEL[sectionType]}</span>
              )}
            </span>
            <button
              onClick={stopRecording}
              className="ml-auto text-[10px] underline text-white/70 hover:text-white"
              data-testid="section-stop-link"
            >
              Stop & preview
            </button>
          </div>
          <p className="text-sm leading-snug text-white/95 line-clamp-3">
            {livePartial || <span className="italic text-white/40">Start speaking… your words will appear here in real time.</span>}
          </p>
        </div>
      )}

      <SectionDictationPreviewModal
        open={previewOpen}
        sectionType={sectionType}
        uploading={uploading}
        refining={refining}
        rawText={rawText}
        refinedText={refinedText}
        view={view}
        edited={edited}
        onChangeView={(v) => {
          setView(v);
          setEdited(v === 'refined' && refinedText ? refinedText : rawText);
        }}
        onChangeEdited={setEdited}
        onClose={() => setPreviewOpen(false)}
        onApply={handleApply}
      />
    </>
  );
}

// ===================== Preview Modal =====================

function SectionDictationPreviewModal({
  open, sectionType,
  uploading, refining,
  rawText, refinedText, view, edited,
  onChangeView, onChangeEdited,
  onClose, onApply,
}) {
  if (!open) return null;
  const sectionLabel = SECTION_LABEL[sectionType] || 'Dictation';
  const hasRefined = !!refinedText && refinedText !== rawText;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border-0 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="section-dictation-preview"
      >
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-iith-navy via-iith-navyMid to-[#0A1F38] text-white px-6 pt-4 pb-3 flex-shrink-0">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-iith-saffron/20 rounded-full blur-3xl" />
          <div className="flex items-center justify-between relative">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center">
                <Wand2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold leading-tight" style={{ fontFamily: 'Manrope' }}>
                  {sectionLabel} · Dictation Preview
                </h3>
                <p className="text-[11px] text-white/75">
                  Review what AI heard. Edit inline. Then apply to the section.
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="section-preview-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Status strip */}
        <div className="px-6 py-2 bg-slate-50 border-b flex items-center gap-3 text-[11px]">
          {uploading ? (
            <div className="flex items-center gap-1.5 text-iith-navy font-semibold" data-testid="section-preview-uploading">
              <Loader2 className="h-3 w-3 animate-spin" /> Transcribing audio…
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-emerald-700 font-semibold">
              <Check className="h-3 w-3" /> Whisper ready
            </div>
          )}
          {sectionType && SECTION_LABEL[sectionType] && (
            refining ? (
              <div className="flex items-center gap-1.5 text-iith-saffron font-semibold" data-testid="section-preview-refining">
                <Sparkles className="h-3 w-3 animate-pulse" /> AI cleaning for {SECTION_LABEL[sectionType]}…
              </div>
            ) : hasRefined ? (
              <div className="flex items-center gap-1.5 text-iith-saffron font-semibold">
                <Sparkles className="h-3 w-3" /> AI refined
              </div>
            ) : !uploading && rawText ? (
              <div className="text-slate-500 italic">No AI changes needed</div>
            ) : null
          )}

          {/* Raw / refined toggle */}
          {hasRefined && (
            <div className="ml-auto inline-flex rounded-md bg-white border border-slate-200 overflow-hidden">
              <button
                onClick={() => onChangeView('refined')}
                className={`text-[11px] px-2 py-1 font-semibold transition ${view === 'refined' ? 'bg-iith-saffron text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid="section-preview-view-refined"
              >
                AI cleaned
              </button>
              <button
                onClick={() => onChangeView('raw')}
                className={`text-[11px] px-2 py-1 font-semibold transition ${view === 'raw' ? 'bg-slate-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid="section-preview-view-raw"
              >
                Raw
              </button>
            </div>
          )}
        </div>

        {/* Editable body */}
        <div className="flex-1 px-6 py-4 overflow-y-auto bg-slate-50/40 min-h-0">
          {uploading && !rawText ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Loader2 className="h-8 w-8 animate-spin mb-3 text-iith-saffron" />
              <p className="text-sm">Listening to your voice…</p>
              <p className="text-[11px] mt-1">Typically takes ~1 second</p>
            </div>
          ) : !rawText ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <AlertCircle className="h-8 w-8 mb-2 text-slate-300" />
              <p className="text-sm italic">No transcript yet.</p>
            </div>
          ) : (
            <>
              <Textarea
                value={edited}
                onChange={(e) => onChangeEdited(e.target.value)}
                placeholder="Your dictation appears here…"
                className="min-h-[180px] text-sm leading-relaxed bg-white border-slate-200 focus-visible:ring-iith-saffron"
                data-testid="section-preview-editable"
              />
              {hasRefined && view === 'refined' && (
                <p className="text-[10px] text-slate-500 mt-2 italic">
                  AI cleaned this for the <b>{sectionLabel}</b> section. Switch to <b>Raw</b> to see the verbatim transcript.
                </p>
              )}
              {view === 'raw' && (
                <p className="text-[10px] text-slate-500 mt-2 italic">
                  Verbatim Whisper output — no AI changes applied.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t flex items-center justify-between bg-white flex-shrink-0">
          <Button variant="ghost" onClick={onClose} className="gap-1.5 text-slate-600" data-testid="section-preview-cancel">
            <X className="h-4 w-4" /> Discard
          </Button>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
              {(edited || '').trim().length} chars
            </Badge>
            <Button
              onClick={onApply}
              disabled={!edited?.trim() || uploading}
              className="bg-gradient-to-r from-iith-navy to-iith-saffron hover:opacity-95 text-white gap-1.5 px-5"
              data-testid="section-preview-apply"
            >
              <Check className="h-4 w-4" />
              Apply to {sectionLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
